/**
 * route.ts — path templating.
 *
 * Contract (§3, §10):
 *   - routeTemplate must be parameterised (/vehicles/:id), NEVER contain
 *     resolved ids or query strings.
 *   - We match against the known route table from src/app/router.tsx.
 *   - Unknown paths produce '/unknown' to avoid leaking free-text URLs.
 *
 * Must be kept in sync with src/app/router.tsx when routes are added/removed.
 */

/** Route templates in order of specificity (longest match wins). */
const ROUTE_TEMPLATES: Array<{ pattern: RegExp; template: string }> = [
  { pattern: /^\/vehicles\/new$/, template: '/vehicles/new' },
  { pattern: /^\/vehicles\/[^/]+\/service\/[^/]+$/, template: '/vehicles/:id/service/:entryId' },
  { pattern: /^\/vehicles\/[^/]+\/service$/, template: '/vehicles/:id/service' },
  { pattern: /^\/vehicles\/[^/]+\/diagnose$/, template: '/vehicles/:id/diagnose' },
  { pattern: /^\/vehicles\/[^/]+\/transfer$/, template: '/vehicles/:id/transfer' },
  { pattern: /^\/vehicles\/[^/]+\/projects$/, template: '/vehicles/:id/projects' },
  { pattern: /^\/vehicles\/[^/]+$/, template: '/vehicles/:id' },
  { pattern: /^\/vehicles$/, template: '/vehicles' },
  { pattern: /^\/transfer\/accept$/, template: '/transfer/accept' },
  { pattern: /^\/insights$/, template: '/insights' },
  { pattern: /^\/sign-in$/, template: '/sign-in' },
  { pattern: /^\/$/, template: '/' },
];

/**
 * Map a concrete pathname to its route template.
 * Strips query strings and hashes before matching.
 */
export function toRouteTemplate(pathname: string): string {
  // Strip query string and hash — never include them in telemetry.
  const clean = pathname.split('?')[0]?.split('#')[0] ?? '/';

  for (const { pattern, template } of ROUTE_TEMPLATES) {
    if (pattern.test(clean)) return template;
  }

  return '/unknown';
}

/** Returns the current route template from window.location. */
export function currentRouteTemplate(): string {
  if (typeof window === 'undefined') return '/unknown';
  return toRouteTemplate(window.location.pathname);
}
